import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPhone,
  faEnvelope,
  faMapMarkerAlt,
  faLightbulb,
  faFireFlameCurved,
  faWifi,
  faClock,
  faChevronDown,
  faChevronUp,
} from "@fortawesome/free-solid-svg-icons";
import Logo from "./Logo";
import { motion } from "framer-motion";

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const [isPhoneAccordionOpen, setIsPhoneAccordionOpen] = useState(false);

  const phoneNumbers = [
    { number: "06 92963134", formatted: "+390692963134" },
    { number: "02 40055100", formatted: "+390240055100" },
    { number: "06 92963113", formatted: "+390692963113" },
    { number: "06 60507771", formatted: "+390660507771" },
  ];

  const quickLinks = [
    { to: "/", label: "Home" },
    { to: "/chi-siamo", label: "Chi Siamo" },
    { to: "/offerte", label: "Offerte" },
    { to: "/contattaci", label: "Contattaci" },
  ];

  const services = [
    { icon: faLightbulb, label: "Offerte Luce", to: "/offerte" },
    { icon: faFireFlameCurved, label: "Offerte Gas", to: "/offerte" },
    { icon: faWifi, label: "Offerte Internet", to: "/offerte" },
  ];


  return (
    <footer className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-96 h-96 bg-primary rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-secondary rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10">
        {/* Main Footer */}
        <div className="container mx-auto px-4 pt-20 pb-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
            {/* Company Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-1"
            >
              <div className="mb-6">
                <Logo className="h-12 brightness-0 invert" />
              </div>
              <p className="text-gray-400 leading-relaxed mb-6">
                Il tuo partner di fiducia per le migliori offerte di luce, gas e internet.
                Risparmia sulle bollette con le nostre soluzioni personalizzate.
              </p>

              {/* Dati Aziendali */}
              <div className="space-y-2 text-sm">
                <p className="text-gray-400">
                  <span className="font-semibold text-white">Marchio di proprietà di:</span>
                  <br />
                  Novastart Srl
                </p>
                <p className="text-gray-400">
                  <span className="font-semibold text-white">Sede Legale:</span>
                  <br />
                  Via Privata Del Gonfalone 3<br />
                  Milano (MI) 20123
                </p>
                <p className="text-gray-400">
                  <span className="font-semibold text-white">P.IVA:</span> 03373610785
                </p>
              </div>
            </motion.div>

            {/* Quick Links */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <h3 className="text-lg font-bold mb-6 relative inline-block">
                Link Rapidi
                <span className="absolute -bottom-2 left-0 w-12 h-1 bg-gradient-to-r from-primary to-secondary rounded-full"></span>
              </h3>
              <ul className="space-y-3">
                {quickLinks.map((link, index) => (
                  <li key={index}>
                    <Link
                      to={link.to}
                      className="text-gray-400 hover:text-white transition-colors duration-300 flex items-center group"
                    >
                      <span className="w-0 h-0.5 bg-gradient-to-r from-primary to-secondary transition-all duration-300 group-hover:w-4 mr-0 group-hover:mr-2"></span>
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Services */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <h3 className="text-lg font-bold mb-6 relative inline-block">
                I Nostri Servizi
                <span className="absolute -bottom-2 left-0 w-12 h-1 bg-gradient-to-r from-primary to-secondary rounded-full"></span>
              </h3>
              <ul className="space-y-4">
                {services.map((service, index) => (
                  <li key={index}>
                    <Link
                      to={service.to}
                      className="flex items-center gap-3 text-gray-400 hover:text-white transition-colors duration-300 group"
                    >
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/10 group-hover:bg-gradient-to-r group-hover:from-primary group-hover:to-secondary transition-all duration-300">
                        <FontAwesomeIcon icon={service.icon} className="text-xs" />
                      </div>
                      <span>{service.label}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <h3 className="text-lg font-bold mb-6 relative inline-block">
                Contatti
                <span className="absolute -bottom-2 left-0 w-12 h-1 bg-gradient-to-r from-primary to-secondary rounded-full"></span>
              </h3>
              <ul className="space-y-4">
                {/* Accordion per i numeri di telefono */}
                <li>
                  <div className="bg-white/5 rounded-lg overflow-hidden">
                    <button
                      onClick={() => setIsPhoneAccordionOpen(!isPhoneAccordionOpen)}
                      className="w-full flex items-center justify-between gap-3 p-3 text-gray-400 hover:text-white transition-colors duration-300 group"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-white/10 group-hover:bg-gradient-to-r group-hover:from-primary group-hover:to-secondary transition-all duration-300 flex-shrink-0">
                          <FontAwesomeIcon icon={faPhone} className="text-sm" />
                        </div>
                        <div className="text-left">
                          <div className="text-xs text-gray-500 mb-1">Numeri di telefono</div>
                          <div className="font-medium">{phoneNumbers[0].number}</div>
                        </div>
                      </div>
                      <FontAwesomeIcon 
                        icon={isPhoneAccordionOpen ? faChevronUp : faChevronDown} 
                        className="text-sm transition-transform duration-300"
                      />
                    </button>
                    
                    {/* Lista numeri accordion */}
                    {isPhoneAccordionOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="border-t border-white/10 px-3 py-2 space-y-2"
                      >
                        {phoneNumbers.map((phone, index) => (
                          <a
                            key={index}
                            href={`tel:${phone.formatted}`}
                            className="block py-2 px-3 text-gray-400 hover:text-white hover:bg-white/5 rounded-lg transition-all duration-300"
                          >
                            <FontAwesomeIcon icon={faPhone} className="mr-2 text-xs text-primary" />
                            {phone.number}
                          </a>
                        ))}
                      </motion.div>
                    )}
                  </div>
                </li>
                <li>
                  <a
                    href="mailto:info@servizioclientienergia.com"
                    className="flex items-start gap-3 text-gray-400 hover:text-white transition-colors duration-300 group"
                  >
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-white/10 group-hover:bg-gradient-to-r group-hover:from-primary group-hover:to-secondary transition-all duration-300 flex-shrink-0">
                      <FontAwesomeIcon icon={faEnvelope} className="text-sm" />
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 mb-1">Email</div>
                      <div className="font-medium break-all">info@servizioclientienergia.com</div>
                    </div>
                  </a>
                </li>
                <li>
                  <div className="flex items-start gap-3 text-gray-400">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-white/10 flex-shrink-0">
                      <FontAwesomeIcon icon={faMapMarkerAlt} className="text-sm" />
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 mb-1">Sede Operativa</div>
                      <div className="font-medium">Via Filippo Argelati 10<br />Milano, Italia</div>
                    </div>
                  </div>
                </li>
                <li>
                  <div className="flex items-start gap-3 text-gray-400">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-white/10 flex-shrink-0">
                      <FontAwesomeIcon icon={faClock} className="text-sm" />
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 mb-1">Orari</div>
                      <div className="font-medium text-sm">
                        Lun-Ven: 9:00-20:00<br />
                        Sab: 9:00-14:00
                      </div>
                    </div>
                  </div>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10">
          <div className="container mx-auto px-4 py-6">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-gray-400 text-sm text-center md:text-left">
                &copy; {currentYear} <span className="font-semibold text-white">Servizio Clienti Energia</span>. 
                Tutti i diritti riservati.
              </p>
              <div className="flex flex-wrap justify-center gap-6 text-sm">
                <Link
                  to="/privacy-policy"
                  className="text-gray-400 hover:text-white transition-colors duration-300"
                >
                  Privacy Policy
                </Link>
                <Link
                  to="/cookie-policy"
                  className="text-gray-400 hover:text-white transition-colors duration-300"
                >
                  Cookie Policy
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Gradient Line */}
        <div className="h-1 bg-gradient-to-r from-primary via-secondary to-accent"></div>
      </div>
    </footer>
  );
};

export default Footer;
